import { Button } from 'antd';
import routePaths from 'config/routes';
import Router from 'next/router';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import redirectTo from 'services/redirectTo';
import {
  setShowDiscoveryModal,
  setUserCampaigns,
} from 'src/actions/appActions';
import { updateUserCoupons } from 'src/actions/authActions';
import { absPoints, useWindowSize } from 'utils';
import * as api from 'services/api';
import classNames from 'classnames';
import { config } from 'config/config';
import { ConsumableNatureEnum } from 'src/shared/enums';

export interface DiscoveryCouponData {
  consumableName: string;
  firstCoupon: boolean;
  consumableImg: string;
  points: number;
  campaignId: string;
  consumableId: number;
  consumableProduct: any;
  pointsOverwrite?: string;
}

type Props = ReturnType<typeof mapDispatchToProps> & DiscoveryCouponData;

const DiscoveryCoupon: FC<Props> = ({
  consumableName,
  firstCoupon,
  consumableImg,
  points,
  campaignId,
  consumableId,
  consumableProduct,
  pointsOverwrite,
  setShowDiscoveryModal,
  setUserCampaigns,
  updateUserCoupons,
}) => {
  const { formatMessage } = useIntl();

  const [disabled, setDisabled] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [consumableData, setConsumableData] = useState({
    description: '',
    media: '',
    stats: [],
  });

  const { isLGBG } = useWindowSize();

  useEffect(() => {
    setConsumableData(consumableProduct);
  }, []);

  const discoveryPointsInfo = (points: number) => {
    return (
      <div className="discovery-points">
        <div className="discovery-points-icon">
          <div className="icon-points challengeCard__points--icon challengeCard__icon--small challengeCard__icon--points" />
        </div>
        <div className="discovery-points-text">
          <span className="discovery-absolute-points">
            {pointsOverwrite ? (
              <>&nbsp;{pointsOverwrite}</>
            ) : (
              absPoints(Number(points)).toLocaleString()
            )}
          </span>
          <span>
            <FormattedMessage id="consumable.coupon.points" />
          </span>
        </div>
      </div>
    );
  };

  const productStatsDropdownButton = () => {
    return (
      <div
        className="product-stats-dropdown-button"
        onClick={clickProductStatsDropdown}>
        <div className="product-stats-dropdown-text">
          <FormattedMessage id="consumable.coupon.discovery.dropdown.button" />
        </div>
        <div className="product-stats-dropdown-icon-container">
          <div className={buttonClassName} />
        </div>
      </div>
    );
  };

  const buttonClassName = classNames('icon icon--arrow-dropdown', {
    'product-stats-dropdown-icon icon--arrow-dropdown-up': showStats,
    'product-stats-dropdown-icon icon--arrow-dropdown-down': !showStats,
  });

  const couponTopClassName = classNames('discovery-coupon-top', {
    'discovery-coupon-top-mobile-padding': !showStats,
  });

  const clickProductStatsDropdown = () => {
    setShowStats(!showStats);
  };

  const consumableProductStats = () => {
    return (
      <div className="discovery-coupon-stats">
        <div className="consumable-product-stats-container">
          <div className="consumable-product-stats-section">
            <div className="consumable-product-stats-title">
              <FormattedMessage id="consumable.coupon.discovery.product.results" />
            </div>
            <div className="consumable-product-stats-list">
              {consumableData?.stats.map((stat) => (
                <div className="consumable-product-stat">
                  <div className="consumable-product-stat-percentage">
                    {stat.stat}
                  </div>
                  <div className="consumable-product-stat-effect">
                    {stat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <img
            className="consumable-product-image"
            src={consumableData?.media}
          />
        </div>
      </div>
    );
  };

  const greenBannerText = () => {
    return (
      <div className="discovery-green-text">
        <span className="challengeCard__points--text">
          <FormattedMessage id="consumable.coupon.discovery.most.chosen" />
        </span>
      </div>
    );
  };

  const clickDiscoverCoupon = async () => {
    const joinCampaign = {
      consumableId: consumableId,
      campaignId: campaignId,
    };

    try {
      setDisabled(true);
      await api.joinCampaign(joinCampaign);
    } catch (err) {
      return;
    } finally {
      setDisabled(false);
    }

    updateUserCoupons();
    setUserCampaigns();
    setShowDiscoveryModal(false);
    redirectTo(routePaths.PAGES.MY_ACCOUNT.MY_BONUS, undefined, Router);
  };

  return (
    <div className="discovery-coupon-container">
      <div className={couponTopClassName}>
        <div className="discovery-image-container">
          <div className="discovery-most-chosen-container">
            {firstCoupon && !isLGBG && greenBannerText()}
          </div>
          <img className="discovery-image" src={consumableImg} />
        </div>
        <div className="discovery-product-information-container">
          <div className="discovery-text-container">
            {firstCoupon && isLGBG && greenBannerText()}
            <h2 className="discovery-consumable-name">{consumableName}</h2>
            <p className="discovery-consumable-product-description">
              {consumableData?.description}
            </p>
          </div>
          <div className="discovery-dropdown-points">
            <div className="product-stats-dropdown-container">
              {!!consumableData?.stats.length && productStatsDropdownButton()}
            </div>
            <div className="discovery-points-container">
              {discoveryPointsInfo(points)}
            </div>
          </div>
        </div>
      </div>
      {showStats && consumableProductStats()}
      <div className="discovery-button-container">
        <Button
          className="btn-primary--black qr-landing__login__form__button__logged discovery-button"
          disabled={disabled}
          onClick={clickDiscoverCoupon}>
          {formatMessage({
            id: 'consumable.coupon.discovery.button',
          })}
        </Button>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    { setShowDiscoveryModal, setUserCampaigns, updateUserCoupons },
    dispatch,
  );

export default connect(null, mapDispatchToProps)(DiscoveryCoupon);
